import React, { useState, useEffect, useCallback } from 'react';
import './TripGame.css';


function TripGame({ game, participants, user, onAnswer, onEnd }) {
  const [timeRemaining, setTimeRemaining] = useState(game.timeLimit);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);

  // Check if user is the one who added the song (can't answer)
  const isRequester = game.song.addedBy === user.uid;

  // Countdown timer
  useEffect(() => {
    if (hasAnswered || showResult || isRequester) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnswered, showResult, isRequester]);

  // Handle time running out
  const handleTimeUp = useCallback(async () => {
    if (hasAnswered) return;
    
    setHasAnswered(true);
    const gameResult = await onAnswer(null, 0);
    setResult(gameResult);
    setShowResult(true);

    // Auto-close after showing results
    setTimeout(() => {
      onEnd();
    }, 3000);
  }, [hasAnswered, onAnswer, onEnd]);

  // Handle answer selection
  const handleSelectAnswer = async (answerId) => {
    if (hasAnswered || isRequester) return;

    setSelectedAnswer(answerId);
    setHasAnswered(true);

    const gameResult = await onAnswer(answerId, timeRemaining);
    setResult(gameResult);
    
    // Show result after a brief moment
    setTimeout(() => {
      setShowResult(true);
      
      // Auto-close after showing results
      setTimeout(() => {
        onEnd();
      }, 3000);
    }, 500);
  };

  // Get display name for a participant
  const getParticipantName = (uid) => {
    const p = participants.find(p => p.uid === uid);
    return p ? p.name : 'Unknown';
  };

  // Get correct answer name
  const correctAnswerName = getParticipantName(game.correctAnswer);

  // Create answer options: All players EXCEPT the requester should be available as choices
  // Each player also excludes themselves from their own choices
  // So if Player A requested: 
  //   - Player A sees "wait while others guess" (handled by isRequester)
  //   - Player B sees: Player A (requester), Player C, Player D, etc. (all except Player B themselves)
  //   - Player C sees: Player A (requester), Player B, Player D, etc. (all except Player C themselves)
  const allParticipants = participants || [];
  
  // Filter: Show all participants except the current user (you can't guess yourself)
  // Note: The requester IS included as a choice for others to guess
  const answerOptions = allParticipants.filter(p => p.uid !== user.uid);

  // If no answer options, show all participants or at least an "Unknown" option
  let displayOptions = answerOptions;
  if (displayOptions.length === 0) {
    // If somehow no other participants, show all participants as options
    displayOptions = allParticipants.length > 0 
      ? allParticipants 
      : [{ uid: 'unknown', name: 'Unknown' }];
  }

  // Debug logging
  console.log('🎮 TripGame Debug:', {
    totalParticipants: allParticipants.length,
    participantNames: allParticipants.map(p => p.name || p.email),
    answerOptions: answerOptions.length,
    displayOptions: displayOptions.length,
    displayOptionNames: displayOptions.map(p => p.name || p.email),
    songAddedBy: game.song.addedBy,
    currentUser: user.uid,
    currentUserName: user.displayName || user.email,
    isRequester
  });

  return (
    <div className="trip-game-overlay">
      <div className="trip-game">
        {/* Game Header */}
        <div className="game-header">
          <div className="game-type">
            🎮 {game.type === 'whoRequested' ? 'WHO REQUESTED THIS?' : 'TRIVIA'}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!showResult && !isRequester && (
              <div className={`game-timer ${timeRemaining <= 3 ? 'urgent' : ''}`}>
                ⏱️ {timeRemaining}s
              </div>
            )}
            {/* Skip button - only show if host or if stuck */}
            <button
              className="game-skip-btn"
              onClick={() => {
                if (window.confirm('Skip this game and play the song?')) {
                  onEnd();
                }
              }}
              title="Skip game"
            >
              ⏭️ Skip
            </button>
          </div>
        </div>

        {/* Song Info */}
        <div className="game-song-info">
          <div className="song-emoji">🎵</div>
          <div className="song-title">{game.song.title}</div>
          <div className="song-artist">by {game.song.artist}</div>
        </div>

        {/* If user is the requester */}
        {isRequester && !showResult && (
          <div className="requester-message">
            <div className="requester-icon">🤫</div>
            <p>You added this song!</p>
            <p className="requester-hint">Wait while others guess...</p>
            <div className="waiting-dots">
              <span>.</span><span>.</span><span>.</span>
            </div>
          </div>
        )}

        {/* Answer Options */}
        {!showResult && !isRequester && (
          <div className="game-options">
            <p className="options-prompt">Tap your answer:</p>
            {displayOptions.length > 0 ? (
              <div className="options-grid">
                {displayOptions.map((participant, index) => (
                  <button
                    key={participant.uid || `option-${index}`}
                    className={`option-btn ${selectedAnswer === participant.uid ? 'selected' : ''} ${hasAnswered ? 'disabled' : ''}`}
                    onClick={() => handleSelectAnswer(participant.uid)}
                    disabled={hasAnswered}
                  >
                    <span className="option-emoji">
                      {['😎', '👩', '🧔', '👧', '🧑', '👨', '👩‍🦰', '🧑‍🦱', '🤷'][index] || '🎵'}
                    </span>
                    <span className="option-name">{participant.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="no-options-message">
                <p>No other participants to guess from.</p>
                <button
                  className="skip-to-song-btn"
                  onClick={() => onEnd()}
                >
                  Skip to Song
                </button>
              </div>
            )}
          </div>
        )}

        {/* Answered Status */}
        {hasAnswered && !showResult && !isRequester && (
          <div className="answered-waiting">
            <div className="check-icon">✓</div>
            <p>Answer locked in!</p>
            <p className="waiting-text">Waiting for others...</p>
          </div>
        )}

        {/* Result Screen */}
        {showResult && (
          <div className="game-result">
            <div className="result-header">🎉 TIME'S UP!</div>
            
            <div className="correct-answer">
              <p>The correct answer is...</p>
              <div className="answer-reveal">
                <span className="star">⭐</span>
                <span className="answer-name">{correctAnswerName}</span>
                <span className="star">⭐</span>
              </div>
            </div>

            {!isRequester && result && (
              <div className={`your-result ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                {result.isCorrect ? (
                  <>
                    <span className="result-icon">✓</span>
                    <span>Correct! +{result.points} pts</span>
                  </>
                ) : (
                  <>
                    <span className="result-icon">✗</span>
                    <span>Wrong! +0 pts</span>
                  </>
                )}
              </div>
            )}

            <div className="next-song-timer">
              ▶ Playing song in 3 seconds...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TripGame;

